import { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useAuthStore } from '@/store/authStore'
import { ClientSelectionPage } from './order/ClientSelectionPage'
import { CategorySelectionPage } from './order/CategorySelectionPage'
import { TemplateSelectionPage } from './order/TemplateSelectionPage'
import { AppointmentConfigurationPage } from './order/AppointmentConfigurationPage'
import { BeneficiarySelectionPage } from './order/BeneficiarySelectionPage'
import { QuestionsPage } from './order/QuestionsPage'
import { PaymentPage } from './order/PaymentPage'

/**
 * Business clients order for themselves: skip client search and seed the store
 * with profile_id used as business_client_profile_id on APIs.
 */
function BusinessClientCreateOrderEntry() {
  const navigate = useNavigate()
  const { businessClientProfile } = useProfileSwitch()
  const setBusinessClientSelfOrder = useOrderCreationStore((s) => s.setBusinessClientSelfOrder)
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    const pid = businessClientProfile?.profile_id
    if (!pid) {
      navigate('/dashboard', { replace: true })
      return
    }
    setBusinessClientSelfOrder(
      pid,
      businessClientProfile.display_name ?? '',
      user?.phone_number ?? null,
      user?.email ?? null,
      businessClientProfile.business_account_id ?? null
    )
    navigate('/create-order/category', { replace: true })
  }, [
    businessClientProfile?.profile_id,
    businessClientProfile?.display_name,
    businessClientProfile?.business_account_id,
    navigate,
    setBusinessClientSelfOrder,
    user?.phone_number,
    user?.email,
  ])

  return null
}

export function CreateOrderPage() {
  const location = useLocation()
  const { locale } = useLocalization()
  const { isBusinessClientMode } = useProfileSwitch()
  const createOrderEntryPath = useOrderCreationStore((s) => s.getCreateOrderEntryPath())
  const path = location.pathname

  const content =
    path === '/create-order' || path === '/create-order/' ? (
      isBusinessClientMode ? (
        <BusinessClientCreateOrderEntry />
      ) : (
        <ClientSelectionPage />
      )
    ) : path === '/create-order/category' ? (
      <CategorySelectionPage />
    ) : path === '/create-order/templates' ? (
      <TemplateSelectionPage />
    ) : path === '/create-order/appointments' ? (
      <AppointmentConfigurationPage />
    ) : path === '/create-order/beneficiary' ? (
      <BeneficiarySelectionPage />
    ) : path === '/create-order/questions' ? (
      <QuestionsPage />
    ) : path === '/create-order/payment' ? (
      <PaymentPage />
    ) : (
      <Navigate to={createOrderEntryPath} replace />
    )

  return <div key={locale}>{content}</div>
}
